import {
  existingEquipmentVulcan,
  existingEquipmentWiredSensors,
  existingEquipmentWirelessSensors,
  existingEquipmentCameras,
  secureBusinessPlanVulcan,
  fieldNewOrExistingCustomer,
  fieldProvinceVUL,
  tsbTerm,
  takeoverEquipmentWired,
  takeoverEquipmentWireless,
  keyMonitorIncludedEquipmentVulcan,
  keyMonitorAdditionalEquipmentVulcan,
  keyProtectIncludedEquipmentVulcan,
  protectIncludedEquipmentCameraSelectionVulcan,
  keyProtectAdditionalEquipmentVulcan,
  keyControlAdditionalEquipmentVulcan,
  smartCameraIncludedEquipmentCameraSelectionVulcan,
  smartCameraAdditionalEquipmentVulcan,
  fieldInstallationFeeAmount,
  fieldInstallationFeePaymentOption,
  installationFeePaymentOptions,
  smokeDetectorWarningNoteVulcan,
  temperatureSensorFeeNote1Vulcan,
  temperatureSensorFeeNote2Vulcan,
  smokeFireDetectorVulcan,
  easyThreeYearSmokeFireDetectorVulcan,
  easyFiveYearSmokeFireDetectorVulcan,
  temperatureSensorVulcan,
  easyThreeYearTemperatureSensorVulcan,
  easyFiveYearTemperatureSensorVulcan
} from '../../../utils/constant';
import { secureBusinessOffersVulcan } from '../../vulcanPricing/secureBusiness/secureBusinessFieldNames';
import { calculateInstallationFee } from '../../vulcanPricing/installationFee/installationFee';
import { handleInstallationFeeDiscount } from '../../vulcanPricing/secureBusiness/secureBusiness';
import { getInstallationFeePaymentType } from '../../vulcanPricing/utils/utils';
import { numberOfSelectedItems } from '../../utils';
import { getControlIncludedCameraTotal } from '../../vulcanHelpers/secureBusinessHelpers';

// The equipment-blob keys `calculateInstallationFee`/`calculateSWT` read.
// These blobs live only in `values` (engine.values) — equipment sections are
// `custom`/blob-shaped (vulcanContentTypeMap.js) and are never tree-walked
// into fieldStates — but they're already in the exact rich shape
// (`{productKey: {value, swtRoc, swtQc}}`) `calculateSWT` expects, same as
// `values[existingEquipmentVulcan]` below.
const INSTALLATION_FEE_EQUIPMENT_KEYS = [
  existingEquipmentVulcan,
  takeoverEquipmentWired,
  takeoverEquipmentWireless,
  keyMonitorIncludedEquipmentVulcan,
  keyMonitorAdditionalEquipmentVulcan,
  keyProtectIncludedEquipmentVulcan,
  protectIncludedEquipmentCameraSelectionVulcan,
  keyProtectAdditionalEquipmentVulcan,
  smartCameraIncludedEquipmentCameraSelectionVulcan,
  smartCameraAdditionalEquipmentVulcan
];

const buildInstallationFeeEquipmentBag = values =>
  INSTALLATION_FEE_EQUIPMENT_KEYS.reduce((acc, key) => {
    acc[key] = values[key] || {};
    return acc;
  }, {});

/**
 * Pure rewrite of `SecureBusinessHelpers` (vulcanHelpers/secureBusinessHelpers.js)
 * as `fieldRules` for the TELUS Secure Business section:
 *  - Offers: filtered when the Smart Camera plan is selected, else the $10 Bundle
 *    (Vulcan only) and $15 Monthly discounts are DISABLED (not removed); the
 *    equipment-count-driven "Waived Installation Fee"/"% Discount" option is
 *    then layered on top via `handleInstallationFeeDiscount`.
 *  - TSB Term: Vulcan drops 60-month and forces 36-month (a self-value derivation).
 *  - Plan: excluded by the customer's existing equipment (sensors/cameras), and
 *    Vulcan additionally drops Smart Camera; the selected plan is cleared if it
 *    lands in the excluded set.
 *  - Installation Fee Amount / Payment Option (SOF-3402/SOF-3439, real-order bug —
 *    fee always showed $0): ported from ~150 lines of per-field special-casing in
 *    formStepVulcan.js's legacy render loop (`feature/uds-migration`, ~lines
 *    2226-2410) that never got a fieldRules replacement when that loop was
 *    trimmed down. `calculateInstallationFee`/`handleInstallationFeeDiscount`
 *    (vulcanPricing/) are reused unchanged. Because fieldRules are strictly
 *    per-field scoped (a rule invocation for field X can only patch X's own
 *    state — confirmed in the package's `buildFieldStates`), the legacy code's
 *    cross-field writes (e.g. writing into the payment-option field's value
 *    from inside the fee-amount block) become independent self-derivations:
 *    each of the three branches below (`secureBusinessOffersVulcan`,
 *    `fieldInstallationFeeAmount`, `fieldInstallationFeePaymentOption`)
 *    recomputes `computeInstallationFee(values)` fresh off `values` rather than
 *    reading another field's derived state — the same pattern as
 *    `deriveStoreOutletCode` in `vulcanFieldRules.js`, and safer than the
 *    legacy evaluation-order dependency it replaces.
 *
 * Notes preserved from the original:
 *  - It reads `formFields[x].value` DIRECTLY (not `getFieldValue`), so there is no
 *    hidden-driving-field visibility gate here.
 *  - `disableOptions` mutated options in place; here we emit fresh option objects
 *    with `disabledOption: true` (no def mutation, no stale flags across renders).
 *  - In the plan exclude path the original returns the ORIGINAL options minus the
 *    excluded values — NOT combined with the Vulcan Smart-Camera filter — so this
 *    faithfully does the same.
 *  - The TSB15 special-outlet takeover-equipment add-on count
 *    (`getTsb15AddOnEquipmentCount`) is NOT included in the offers count here —
 *    it needs `values`-shaped input for an ordinary field
 *    (`existingSecurityEquipment`) that doesn't carry the rich `{value,
 *    isVisible}` shape that helper requires. Not a new regression (it doesn't
 *    work today either) — a narrower, separately-flagged follow-up.
 * Verified by rules/secureBusiness.diff.test.js and
 * secureBusinessInstallationFee.rule.test.js.
 *
 * @param {{orderData: Object, t: Object, locale: string}} ctx
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makeSecureBusinessRule(ctx) {
  const { orderData, t, locale, secureBusinessPlanOptions } = ctx;
  const {
    vulTenDollarBundleDiscountTSB,
    vulFifteenDollarMonthlyDiscountTSB,
    vulSmartCameraTSB,
    vulMonitorTSB,
    vulProtectTSB,
    vulControlTSB,
    vulSixtyMonth,
    vulThirtyMonth,
    vulThreeMonthsFreeTSB,
    vulTSBProtect5Off60,
    vulNewCustomer,
    vulOntario,
    vulWaivedInstallationFeeOptionTSB
  } = t;

  const computeInstallationFee = values =>
    calculateInstallationFee(
      buildInstallationFeeEquipmentBag(values),
      values[secureBusinessPlanVulcan]
    );

  const setDisabled = (options, targets) =>
    options.map(o =>
      targets.includes(o.fields.value)
        ? { ...o, fields: { ...o.fields, disabledOption: true } }
        : o
    );

  // The $10 Vulcan/WLN-bundle discount, applied to a plan OPTION's own
  // (label-parsed) monthly price. The discount decision doesn't vary per
  // option — it's driven by which plan is CURRENTLY SELECTED — so this is
  // shared between pricing every option on `secureBusinessPlanVulcan`'s own
  // branch and pricing just the selected one for "3 Months Free" below.
  // Faithful port of formStepVulcan.js's legacy render loop
  // (feature/uds-migration, ~lines 1704-1725), never carried into fieldRules.
  const computeOptionMonthlyPayment = (
    option,
    selectedPlanValue,
    isVulcan,
    offersValue
  ) => {
    const match = option.fields.label && option.fields.label.match(/\$(\d+)/);
    const price = match ? parseInt(match[1], 10) : 0;
    if (selectedPlanValue === vulSmartCameraTSB) return price;
    const hasTenDollarBundleInWLN =
      !isVulcan && (offersValue || []).includes(vulTenDollarBundleDiscountTSB);
    const applyTenDollarDiscount = isVulcan || hasTenDollarBundleInWLN;
    return applyTenDollarDiscount ? price - 10 : price;
  };

  return (field, state, values) => {
    const { name } = field;
    const isVulcan = orderData.isThisVulcanOrder;
    const options = state.options || field.options || [];

    if (name === secureBusinessOffersVulcan) {
      const plan = values[secureBusinessPlanVulcan];
      if (!plan) return undefined;

      let filteredOptions;
      if (plan === vulSmartCameraTSB) {
        filteredOptions = options.filter(
          o =>
            ![
              vulTenDollarBundleDiscountTSB,
              vulFifteenDollarMonthlyDiscountTSB
            ].includes(o.fields.value)
        );
      } else {
        const toDisable = isVulcan
          ? [vulTenDollarBundleDiscountTSB, vulFifteenDollarMonthlyDiscountTSB]
          : [vulFifteenDollarMonthlyDiscountTSB];
        filteredOptions = setDisabled(options, toDisable);
      }

      // Plan-change default offers (formStepVulcan.js's legacy render loop,
      // ~lines 1355-1391): selecting a plan pre-checks its mandatory
      // discount(s) — previously only DISABLED (via `setDisabled` above),
      // never actually pre-selected, so they rendered disabled-but-unchecked
      // instead of disabled-and-checked. Applied only while the offers value
      // is still empty (the fieldRules equivalent of a one-time reset on plan
      // change — same gate as fibreBusinessWifi.js's accessPoints default;
      // a fieldRule re-runs every render, so "still empty" is what stops it
      // from stomping a later user edit). Control has no default (legacy quirk
      // preserved — its switch had no Control case either).
      let defaultOffers;
      if (plan === vulSmartCameraTSB) {
        defaultOffers = [vulWaivedInstallationFeeOptionTSB];
      } else if (plan === vulMonitorTSB || plan === vulProtectTSB) {
        defaultOffers = isVulcan
          ? [vulTenDollarBundleDiscountTSB, vulFifteenDollarMonthlyDiscountTSB]
          : [vulFifteenDollarMonthlyDiscountTSB, vulWaivedInstallationFeeOptionTSB];
      }
      const isOffersEmpty =
        !state.value || (Array.isArray(state.value) && state.value.length === 0);

      // TSBPROTECT5OFF60 ($5 off TELUS Secure Business Protect) eligibility
      // gate (spec 041-tsb-protect-discount). The offer itself is
      // Contentful-driven (an option with monthlyPayment = -5); only exposed
      // when every business rule is met, and dropped from an in-progress
      // selection once it becomes ineligible. Faithful port of
      // formStepVulcan.js's legacy render loop (feature/uds-migration,
      // ~lines 2423-2470), never carried into fieldRules.
      let offersValueForGate =
        isOffersEmpty && defaultOffers ? defaultOffers : state.value;
      // FR-010: mutually exclusive with "3 Months Free" (the only
      // conflicting offer selectable in this field — bill credits are
      // HSIA/fibre offers in other fields and cannot appear here).
      const hasConflictingOffer =
        Array.isArray(offersValueForGate) &&
        offersValueForGate.includes(vulThreeMonthsFreeTSB);
      // FR-009: at least one camera must be added under Protect additional
      // equipment. Reuses the canonical camera counter, which only counts
      // items whose Contentful productType is in cameraTypeList (['Camera',
      // 'PoE Camera']) rather than every additional-equipment item.
      const protectAdditionalCameras = getControlIncludedCameraTotal(
        values[keyProtectAdditionalEquipmentVulcan] || {}
      );
      const isEligibleForTSBProtect5Off60 =
        plan === vulProtectTSB &&
        (values[fieldProvinceVUL] || orderData[fieldProvinceVUL]) ===
          vulOntario &&
        values[fieldNewOrExistingCustomer] === vulNewCustomer &&
        values[tsbTerm] === vulSixtyMonth &&
        protectAdditionalCameras > 0 &&
        !hasConflictingOffer;
      if (!isEligibleForTSBProtect5Off60) {
        // Hide the option (non-destructive) so it reappears automatically
        // once the order becomes eligible again.
        filteredOptions = filteredOptions.filter(
          o => o.fields.label !== vulTSBProtect5Off60
        );
        // Drop a previously-selected offer so it is not applied/persisted.
        if (Array.isArray(offersValueForGate)) {
          offersValueForGate = offersValueForGate.filter(
            offer => offer !== vulTSBProtect5Off60
          );
        }
      }

      // Layer the equipment-count-driven "Waived Installation Fee" / "%
      // Discount for Installation Fee" option on top of the plan-based
      // filter/disable above — ports `handleInstallationFeeDiscount`
      // (secureBusiness.js), previously only ever called from a dead import
      // in formStepVulcan.js, never actually wired into this engine path.
      const additionalEquipmentKeyByPlan = {
        [vulMonitorTSB]: keyMonitorAdditionalEquipmentVulcan,
        [vulProtectTSB]: keyProtectAdditionalEquipmentVulcan,
        [vulSmartCameraTSB]: smartCameraAdditionalEquipmentVulcan
      };
      // Control isn't cased here — preserved legacy quirk (formStepVulcan.js's
      // switch had no Control case either, `default: 0`).
      const count = numberOfSelectedItems(
        values[additionalEquipmentKeyByPlan[plan]]
      );
      const isPercentageDiscount = [
        vulMonitorTSB,
        vulProtectTSB,
        vulSmartCameraTSB
      ].includes(plan);
      const fee = computeInstallationFee(values);

      const scratch = { options: filteredOptions, value: offersValueForGate };
      handleInstallationFeeDiscount(
        scratch,
        count,
        isPercentageDiscount,
        values[fieldInstallationFeePaymentOption],
        fee
      );

      // "3 Months Free": credits the CURRENTLY SELECTED plan's own
      // (discounted) monthly price back as this offer's monthlyPayment — a
      // full offset, not a stored Contentful value. Needs the plan field's
      // OWN option/label data, which isn't visible from THIS field's rule
      // invocation (fieldRules only ever sees the field being evaluated) —
      // threaded in via ctx.secureBusinessPlanOptions
      // (VulcanFormStepRunner.js). Faithful port of formStepVulcan.js's
      // legacy render loop (feature/uds-migration, ~lines 1671-1697),
      // including its label (not value) match for the target option.
      if (
        Array.isArray(scratch.value) &&
        scratch.value.includes(vulThreeMonthsFreeTSB)
      ) {
        const selectedPlanOption = (secureBusinessPlanOptions || []).find(
          o => o.fields.value === plan
        );
        if (selectedPlanOption) {
          const threeMonthsFreeTSBPrice = computeOptionMonthlyPayment(
            selectedPlanOption,
            plan,
            isVulcan,
            scratch.value
          );
          scratch.options = scratch.options.map(o =>
            o.fields.label === vulThreeMonthsFreeTSB
              ? {
                  ...o,
                  fields: {
                    ...o.fields,
                    monthlyPayment: -threeMonthsFreeTSBPrice
                  }
                }
              : o
          );
        }
      }

      return { options: scratch.options, value: scratch.value };
    }

    if (name === fieldInstallationFeeAmount) {
      // Nothing to install without a selected plan — `calculateInstallationFee`
      // has no gate of its own (the legacy render loop didn't need one: this
      // field's block only ever ran while the render loop was actually
      // iterating the TSB step's OWN fields). The whole-order engine puts
      // every step's fields in `fieldStates` at once regardless of which step
      // is showing, so without this gate the $199 installationFeeMinimum
      // floor priced into "Due Now" on EVERY order, whether or not Secure
      // Business was even selected. Confirmed real-data bug.
      if (!values[secureBusinessPlanVulcan]) {
        return { value: 0, oneTimePrice: 0, monthlyPayment: 0 };
      }
      const fee = computeInstallationFee(values);
      const paymentType = getInstallationFeePaymentType(
        values[fieldInstallationFeePaymentOption]
      );
      let oneTimePrice = 0;
      let monthlyPayment = 0;
      if (paymentType === 'oneTime') {
        oneTimePrice = fee;
      } else if (paymentType === 'threeYearEasyPayment') {
        monthlyPayment = Math.round((fee / 36) * 100) / 100;
      }
      // Installation fee over $3000 isn't eligible for 3-year easy payment —
      // force one-time pricing here regardless of the payment-option field's
      // current value (that field independently self-resets in its own
      // branch below; this field can't rely on that having already run this
      // render pass, so it re-derives the same override directly).
      if (fee > 3000) {
        oneTimePrice = fee;
        monthlyPayment = 0;
      }
      return { value: fee, oneTimePrice, monthlyPayment };
    }

    if (name === fieldInstallationFeePaymentOption) {
      // Same gate as fieldInstallationFeeAmount above — without a selected
      // plan there's no real fee to threshold-check, just the SWT floor.
      if (!values[secureBusinessPlanVulcan]) return undefined;
      const fee = computeInstallationFee(values);
      if (fee > 3000 && Array.isArray(options)) {
        const easyPayValues = [
          installationFeePaymentOptions.threeYear,
          installationFeePaymentOptions.threeYearFR
        ];
        const filtered = options.filter(
          o => !(o && o.fields && easyPayValues.includes(o.fields.value))
        );
        const patch = { options: filtered };
        if (easyPayValues.includes(values[fieldInstallationFeePaymentOption])) {
          patch.value =
            locale === 'fr-CA'
              ? installationFeePaymentOptions.oneTimeFR
              : installationFeePaymentOptions.oneTime;
        }
        return patch;
      }
      return undefined;
    }

    if (name === tsbTerm && isVulcan) {
      return {
        options: options.filter(o => o.fields.value !== vulSixtyMonth),
        value: vulThirtyMonth
      };
    }

    if (name === secureBusinessPlanVulcan) {
      const eq = values[existingEquipmentVulcan];
      let excludeValues = [];
      if (eq) {
        const wired =
          Number(
            eq[existingEquipmentWiredSensors] &&
              eq[existingEquipmentWiredSensors].value
          ) || 0;
        const wireless =
          Number(
            eq[existingEquipmentWirelessSensors] &&
              eq[existingEquipmentWirelessSensors].value
          ) || 0;
        const cameras =
          Number(
            eq[existingEquipmentCameras] && eq[existingEquipmentCameras].value
          ) || 0;
        const hasSensors = wired > 0 || wireless > 0;
        const hasCameras = cameras > 0;
        if (hasSensors && hasCameras)
          excludeValues = [vulSmartCameraTSB, vulMonitorTSB];
        else if (hasSensors) excludeValues = [vulSmartCameraTSB];
        else if (hasCameras) excludeValues = [vulMonitorTSB];
      }
      let filteredOptions = options;
      let clearedValue;
      if (excludeValues.length > 0) {
        filteredOptions = options.filter(
          o => !excludeValues.includes(o.fields.value)
        );
        if (excludeValues.includes(values[secureBusinessPlanVulcan]))
          clearedValue = '';
      } else if (isVulcan) {
        filteredOptions = options.filter(
          o => o.fields.value !== vulSmartCameraTSB
        );
      }

      // Dynamic $10 Vulcan/WLN-bundle discount on every option's own
      // (label-parsed) monthly price — see computeOptionMonthlyPayment above.
      // Runs regardless of which branch above fired (exclusion, Smart-Camera
      // drop, or neither), matching the legacy block's unconditional pricing
      // pass over `secureBusinessPlanVulcan.options`.
      const selectedPlan = values[secureBusinessPlanVulcan];
      const offersValue = values[secureBusinessOffersVulcan];
      const pricedOptions = filteredOptions.map(o => ({
        ...o,
        fields: {
          ...o.fields,
          monthlyPayment: computeOptionMonthlyPayment(
            o,
            selectedPlan,
            isVulcan,
            offersValue
          )
        }
      }));

      const patch = { options: pricedOptions };
      if (clearedValue !== undefined) patch.value = clearedValue;
      return patch;
    }

    // Smoke-detector / temperature-sensor notes (Monitor/Protect/Control only —
    // Smart Camera has no additional-equipment key here, same as the
    // installation-fee discount block above): the note is only WORTH showing
    // once the customer actually added the equipment it warns about, not just
    // because a plan is selected. Faithful port of formStepVulcan.js's legacy
    // render loop (~lines 2197-2325) — deferred when inlineFieldRules.js's
    // per-field conditionals were ported (see that file's doc comment) because
    // it needs the equipment section's nested blob, which only lives in
    // `values` (equipment sections are blob-shaped, never tree-walked into
    // fieldStates).
    if (
      [
        smokeDetectorWarningNoteVulcan,
        temperatureSensorFeeNote1Vulcan,
        temperatureSensorFeeNote2Vulcan
      ].includes(name)
    ) {
      const additionalEquipmentKeyByPlan = {
        [vulMonitorTSB]: keyMonitorAdditionalEquipmentVulcan,
        [vulProtectTSB]: keyProtectAdditionalEquipmentVulcan,
        [vulControlTSB]: keyControlAdditionalEquipmentVulcan
      };
      const plan = values[secureBusinessPlanVulcan];
      const section = values[additionalEquipmentKeyByPlan[plan]] || {};
      const qty = f => Number((section[f] && section[f].value) || 0);

      if (name === smokeDetectorWarningNoteVulcan) {
        const hasSmokeDetector =
          qty(smokeFireDetectorVulcan) > 0 ||
          qty(easyThreeYearSmokeFireDetectorVulcan) > 0 ||
          qty(easyFiveYearSmokeFireDetectorVulcan) > 0;
        return hasSmokeDetector ? undefined : { note: '' };
      }

      const sensorCount =
        qty(temperatureSensorVulcan) +
        qty(easyThreeYearTemperatureSensorVulcan) +
        qty(easyFiveYearTemperatureSensorVulcan);
      if (name === temperatureSensorFeeNote1Vulcan) {
        return sensorCount > 0 && sensorCount <= 5 ? undefined : { note: '' };
      }
      // temperatureSensorFeeNote2Vulcan
      return sensorCount > 5 && sensorCount <= 10 ? undefined : { note: '' };
    }

    return undefined;
  };
}

export default makeSecureBusinessRule;
